import { useMemo } from "react";

import { useCurrentStateAndParams } from "@uirouter/react";

import { swSettings } from "app/common/services/swSettings";
import { useApiQuery } from "app/modules/api-client";
import DurationService from "app/services/DurationService";

import type { BenchmarkUrlParams } from "../../../../../types/paramsTypes";
import { useBenchmarkData } from "../../../hooks/useBenchmarkData";
import type { TrafficSource } from "../../../traffic-sources-tabs";
import { countExcelRows } from "../../../utils/countExcelRows";
import { transformData } from "../../../utils/transformData";

export const useShareOfWebsiteTrafficData = (trafficSource: TrafficSource) => {
    const {
        params: { granularity, key, duration },
    } = useCurrentStateAndParams<BenchmarkUrlParams>();

    const { data, isLoading: isDataLoading } = useApiQuery(
        {
            tag: "BenchmarkTracker",
            methodName: "benchmarkTrafficChannelsShareByWebsiteDetail",
            options: {
                enabled: !!swSettings.allowedDuration(duration),
            },
        },
        {
            granularity,
            benchmarkId: key,
            startDate: DurationService.getDurationData(duration).forAPI.iso.from,
        },
    );
    const { icons, primaryDomain, isLoading: isBenchmarkDataLoading } = useBenchmarkData();

    const isLoading = isDataLoading || isBenchmarkDataLoading;

    const transformedData = useMemo(() => {
        if (isLoading || !data || !data[trafficSource]) {
            return [];
        }

        return transformData({ data: data[trafficSource], icons, primaryDomain });
    }, [data, isLoading, icons, primaryDomain, trafficSource]);

    const excelRowCount = useMemo(() => {
        if (!data) {
            return 0;
        }

        return countExcelRows(data);
    }, [data]);

    return {
        isLoading,
        data: transformedData,
        excelRowCount,
    };
};
